import { useMemo, memo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { SupplyDemandPoint } from '../../schemas/gameSave';
import { useChartColors } from '../../hooks/useChartColors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SupplyDemandChartProps {
  data: SupplyDemandPoint[];
  productName?: string;
}

// Format date for chart labels
const formatDateLabel = (date: { day: number; month: number; year: number }): string => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.month - 1]} ${date.day}`;
};

// Convert hex color to rgba with alpha
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const SupplyDemandChart = memo(function SupplyDemandChart({
  data,
  productName
}: SupplyDemandChartProps) {
  // Theme-aware chart colors
  const chartColors = useChartColors();
  const supplyColor = chartColors[3];      // Blue
  const demandColor = chartColors[8];      // Orange
  const salesColor = chartColors[2];       // Green

  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    const labels = data.map(point => formatDateLabel(point.date));

    return {
      labels,
      datasets: [
        {
          label: 'Supply (Inventory)',
          data: data.map(point => point.supply),
          borderColor: supplyColor,
          backgroundColor: hexToRgba(supplyColor, 0.1),
          tension: 0.4,
          fill: false,
          pointRadius: 2,
          pointHoverRadius: 5
        },
        {
          label: 'Demand',
          data: data.map(point => point.demand),
          borderColor: demandColor,
          backgroundColor: hexToRgba(demandColor, 0.1),
          tension: 0.4,
          fill: false,
          pointRadius: 2,
          pointHoverRadius: 5
        },
        {
          label: 'Actual Sales',
          data: data.map(point => point.unitsSold),
          borderColor: salesColor,
          backgroundColor: hexToRgba(salesColor, 0.1),
          tension: 0.4,
          fill: false,
          pointRadius: 2,
          pointHoverRadius: 5,
          borderDash: [5, 5] // Dashed line to differentiate
        }
      ]
    };
  }, [data, supplyColor, demandColor, salesColor]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          font: {
            size: 11
          },
          usePointStyle: true
        }
      },
      tooltip: {
        callbacks: {
          title: (context: { label: string }[]) => {
            return context[0]?.label || '';
          },
          label: (context: { dataset: { label?: string }; parsed: { y: number } }) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString()} units`;
          }
        }
      },
      title: {
        display: !!productName,
        text: productName ? `Supply & Demand - ${productName}` : '',
        font: {
          size: 14
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 10
          },
          // Show fewer labels on smaller datasets
          maxTicksLimit: 10
        },
        grid: {
          display: false
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Units',
          font: {
            size: 11
          }
        },
        ticks: {
          callback: function(tickValue) {
            const value = typeof tickValue === 'number' ? tickValue : parseFloat(String(tickValue));
            if (isNaN(value)) return '';
            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (value >= 1000) {
              return `${(value / 1000).toFixed(1)}K`;
            }
            return value.toString();
          },
          font: {
            size: 10
          }
        },
        beginAtZero: true
      }
    }
  }), [productName]);

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="h-48 flex items-center justify-center bg-theme-bg-secondary rounded-lg">
        <p className="text-theme-text-muted text-sm">
          No supply/demand data available yet. Data will appear after the product starts selling.
        </p>
      </div>
    );
  }

  return (
    <div className="h-48">
      <Line data={chartData} options={chartOptions} />
    </div>
  );
});
